import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { api } from "@api/api";
import { ApiResponse } from "@shared/types";
import { AgreementProduct } from "@modules/agreements/types/agreementProduct";
import { useCloneAgreementMutation } from "@modules/agreements/services/mutations";
import {
  CloneAgreementFormValues,
  cloneAgreementSchema,
} from "@modules/agreements/validations/cloneAgreementSchema";
import { CloneAgreementModalProps } from "./types";

function collectSourceSlugs(products: AgreementProduct[]): string[] {
  const slugs = new Set<string>();

  products.forEach((product) => {
    const slug = product.metadata?.slug?.value;
    if (slug) slugs.add(String(slug).trim());
  });

  return Array.from(slugs).filter(Boolean).sort();
}

async function suggestAvailableAgreementName(sourceName?: string): Promise<string> {
  const base = sourceName?.trim();
  if (!base) return "";

  const { data } = await api.get<ApiResponse<{ name: string }>>(
    "v1/agreement_admin/",
    {
      params: { search: base, page_size: 200 },
    }
  );

  const existingNames = new Set(
    (data.results ?? []).map((agreement) => agreement.name.trim().toLowerCase())
  );

  let count = 2;
  let candidate = `${base} ${count}`;

  while (existingNames.has(candidate.toLowerCase())) {
    count += 1;
    candidate = `${base} ${count}`;
  }

  return candidate;
}

export function useCloneAgreementModal({
  agreementId,
  sourceName,
}: CloneAgreementModalProps) {
  const translate = useTranslate();
  const { open } = useNotification();
  const [isLoadingSlugs, setIsLoadingSlugs] = useState(false);

  const methods = useForm<CloneAgreementFormValues>({
    resolver: zodResolver(cloneAgreementSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      slugEntries: [],
    },
  });

  const { mutate, isPending } = useCloneAgreementMutation();

  const loadSourceSlugs = useCallback(async () => {
    setIsLoadingSlugs(true);

    try {
      const [productsResponse, suggestedName] = await Promise.all([
        api.get<ApiResponse<AgreementProduct>>("v1/product_admin/", {
          params: { agreement: agreementId, page_size: 200 },
        }),
        suggestAvailableAgreementName(sourceName),
      ]);

      const products = productsResponse.data.results ?? [];
      const sourceSlugs = collectSourceSlugs(products);

      methods.reset({
        name: suggestedName,
        slugEntries: sourceSlugs.map((old_slug) => ({
          old_slug,
          new_slug: "",
        })),
      });

      if (!sourceSlugs.length) {
        open?.({
          type: "error",
          message: translate("v1/agreement_admin.messages.clone.no_slugs"),
        });
      }
    } catch {
      open?.({
        type: "error",
        message: translate("v1/agreement_admin.messages.clone.load_error"),
      });
    } finally {
      setIsLoadingSlugs(false);
    }
  }, [agreementId, methods, open, sourceName, translate]);

  const handleConfirm = useCallback(
    (values: CloneAgreementFormValues, options?: any) => {
      const slug_map = values.slugEntries.reduce<Record<string, string>>(
        (acc, entry) => {
          acc[entry.old_slug.trim()] = entry.new_slug.trim();
          return acc;
        },
        {}
      );

      mutate(
        {
          id: agreementId,
          name: values.name.trim(),
          slug_map,
        },
        options
      );
    },
    [agreementId, mutate]
  );

  return {
    methods,
    isPending,
    isLoadingSlugs,
    handleConfirm,
    loadSourceSlugs,
    translate,
  };
}
